'use client';

/**
 * useSubmenuSections Hook
 *
 * Reads submenu navigation items from the NavigationProvider context
 * and builds SubMenuSection[] for use with the SubMenu component.
 *
 * This eliminates the need for static sections.ts files — the same
 * navigation_items table that drives the sidebar also drives submenus.
 *
 * No additional API calls are made; items are filtered from the
 * existing NavigationProvider data.
 */

import { useMemo } from 'react';
import type { SubMenuSection, SubMenuItem } from '@/components/ui/submenu/types';
import type { ResolvedNavItem, NavColorScheme } from './types';
import { useNavigationOptional } from './hooks';

interface UseSubmenuSectionsOptions {
  /** The submenu context to filter by (e.g. 'llm-metrics', 'call-metrics') */
  context: string;

  /** Optional callback to filter/transform the resulting sections */
  filterSections?: (sections: SubMenuSection[]) => SubMenuSection[];
}

interface UseSubmenuSectionsResult {
  /** The resolved submenu sections ready for the SubMenu component */
  sections: SubMenuSection[];

  /** Whether navigation data is still loading */
  isLoading: boolean;
}

/**
 * Hook that reads submenu items from the NavigationProvider and builds
 * SubMenuSection[] for a given context.
 *
 * Items are identified by the `submenuContext` field on ResolvedNavItem.
 * Section headers have no parentKey (within the context), leaf items
 * reference a section header via parentKey.
 */
export function useSubmenuSections(options: UseSubmenuSectionsOptions): UseSubmenuSectionsResult {
  const { context, filterSections } = options;
  const navContext = useNavigationOptional();
  const navigation = navContext?.navigation ?? null;
  const isLoading = navContext?.isLoading ?? true;

  const sections = useMemo(() => {
    if (!navigation) return [];

    // Filter all items belonging to this submenu context
    const contextItems = navigation.allItems.filter(
      (item) => item.submenuContext === context
    );

    if (contextItems.length === 0) return [];

    // Build a set of keys for items in this context (to identify section headers)
    const contextKeys = new Set(contextItems.map((item) => item.key));

    // Section headers: items whose parentKey is NOT in the context set
    // (they either have no parentKey, or their parent is outside this context)
    const sectionHeaders = contextItems.filter(
      (item) => !item.parentKey || !contextKeys.has(item.parentKey)
    );

    // Leaf items: items whose parentKey IS in the context set
    const leafItems = contextItems.filter(
      (item) => item.parentKey && contextKeys.has(item.parentKey)
    );

    // Group leaf items by their parent key
    const childrenByParent = new Map<string, ResolvedNavItem[]>();
    for (const item of leafItems) {
      const list = childrenByParent.get(item.parentKey!) ?? [];
      list.push(item);
      childrenByParent.set(item.parentKey!, list);
    }

    // Build SubMenuSection[] from section headers
    const result: SubMenuSection[] = sectionHeaders
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((header) => {
        const children = (childrenByParent.get(header.key) ?? [])
          .sort((a, b) => a.sortOrder - b.sortOrder);

        const items: SubMenuItem[] = children.map((child) => ({
          id: child.key.replace(`submenu.${context}.`, ''),
          label: child.label,
          path: child.path,
          icon: child.icon ?? undefined,
          description: child.description,
          badge: child.badge?.value != null ? String(child.badge.value) : undefined,
          badgeColor: child.badge?.color as NavColorScheme | undefined,
        }));

        return {
          id: header.key.replace(`submenu.${context}.`, ''),
          label: header.label,
          icon: header.icon ?? undefined,
          colorScheme: header.colorScheme,
          collapsible: header.submenuCollapsible ?? undefined,
          defaultCollapsed: header.submenuDefaultCollapsed ?? undefined,
          items,
        } satisfies SubMenuSection;
      });

    return filterSections ? filterSections(result) : result;
  }, [navigation, context, filterSections]);

  return { sections, isLoading };
}
